// @ts-strict-ignore
import type { Services } from '~/flow/types';
import type {
  LegacyDataRoute,
  ObjectIdTransformationMap,
  DataRoute,
  Action,
} from '../../types';
import { getAttributeToken } from '../../utils';
import { generatePublicIdHash } from '../../../../service/publicIds';
import { transformMLAAttributeComponents, transformAggregationComponents } from '../thresholds';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';

export function transformLegacyDataRoutesToJsonRepresentation({
  dataRoutes,
  transformedActions,
  services,
  legacyActionsToTransformedActionsIdMap,
}: {
  dataRoutes: LegacyDataRoute[];
  transformedActions: Record<string, Action>;
  services: Services;
  legacyActionsToTransformedActionsIdMap: ObjectIdTransformationMap;
}) {
  const transformedJsonDataRoutes = {};
  const legacyDataRoutesToTransformedDataRoutesIdMap = {};

  dataRoutes.forEach(dataRoute => {
    const objectPublicId = dataRoute.publicId
      ? dataRoute.publicId : `dataroute__${generatePublicIdHash(20)}`;
    const sourceId = legacyActionsToTransformedActionsIdMap[dataRoute.sourceId];
    const sourceAction = sourceId
      ? transformedActions[sourceId]
      : null;

    const transformedDataRouteObject: DataRoute = {
      publicId: objectPublicId,
      sourceId,
      targetId: legacyActionsToTransformedActionsIdMap[dataRoute.targetId],
      category: {
        id: dataRoute.category?.id,
        name: dataRoute.category?.name,
      },
      attribute: {
        id: dataRoute.attribute?.id,
        token: getAttributeToken({
          action: sourceAction,
          attributeId: dataRoute.attribute?.id,
          services,
        }),
        displayName: dataRoute.attribute?.display_name,
        attributeName: dataRoute.attribute?.attribute_name,
      },
      aggregationComponents: transformAggregationComponents({
        aggregationComponents: (() => {
          // this is temporary to track down an issue where some data routes have undefined aggregationComponents -- OAK-1150
          const aggComp = dataRoute.aggregationComponents;
          if (!aggComp) {
            captureExceptionsToSentry(
              new Error('Data route aggregation components are undefined.'),
              { dataRouteId: dataRoute.id },
            );
          }
          return aggComp ?? [];
        })(),
      }),
    };

    if (dataRoute.attribute?.multi_level_attribute_filter) {
      transformedDataRouteObject.attribute.multiLevelAttributeFilter = {
        filterComponents: transformMLAAttributeComponents({
          attributeFilterComponents: dataRoute.attribute
            .multi_level_attribute_filter.filter_components,
        }),
      };
    }

    legacyDataRoutesToTransformedDataRoutesIdMap[dataRoute.id] = objectPublicId;
    transformedJsonDataRoutes[objectPublicId] = transformedDataRouteObject;
  });

  return {
    transformedDataRoutes: transformedJsonDataRoutes,
    legacyDataRoutesTransformationMap: legacyDataRoutesToTransformedDataRoutesIdMap,
  };
}
